import { Search } from '@shared/components/ui/Search';
import { SimpleFilter, type SimpleFilterField } from '@shared/components/ui/SimpleFilter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientsDropdown, usePayoutsInfinite } from '../../hooks';
import { PayoutItemsFilters } from '../../models';
import { ExportButton } from './ExportButton';

interface FiltersSectionProps {
  filters: PayoutItemsFilters;
  onFiltersChange: (filters: Partial<PayoutItemsFilters>) => void;
  onResetFilters: () => void;
  isFetching?: boolean;
  exportColumns?: string[];
}

export const FiltersSection = ({
  filters,
  onFiltersChange,
  onResetFilters,
  isFetching = false,
  exportColumns,
}: FiltersSectionProps) => {
  const { t } = useTranslation('codPayoutItems');
  const { clients, isLoading: isLoadingClients } = useClientsDropdown();
  const {
    searchFn: payoutsSearchFn,
    isLoading: isLoadingPayouts,
    hasNextPage: hasMorePayouts,
    onLoadMore: loadMorePayouts,
  } = usePayoutsInfinite();

  // 🎯 Estado local para el campo de búsqueda
  const [searchValue, setSearchValue] = useState(filters.ref || '');

  // 🎯 Sincronizar searchValue con filters.ref
  useEffect(() => {
    setSearchValue(filters.ref || '');
  }, [filters.ref]);

  // 🎯 Handler para el cambio del input de búsqueda
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  // 🎯 Handler para ejecutar búsqueda
  const handleSearchSubmit = useCallback(() => {
    onFiltersChange({ ref: searchValue.trim() || undefined });
  }, [searchValue, onFiltersChange]);

  // 🎯 Handler para reset que también limpia el campo de búsqueda
  const handleReset = useCallback(() => {
    setSearchValue('');
    onResetFilters();
  }, [onResetFilters]);

  // 🎯 Configuración de campos para SimpleFilter (sin el campo ref)
  const fields: SimpleFilterField[] = useMemo(
    () => [
      {
        key: 'client_id',
        label: t('filters.client', 'Cliente'),
        type: 'select',
        options: clients.map((client) => ({
          label: client.name,
          value: String(client.id),
        })),
        allOption: {
          label: t('filters.all_clients', 'Todos los clientes'),
          value: 'all',
        },
        isLoadingOptions: isLoadingClients,
        placeholder: isLoadingClients
          ? t('filters.loading_clients', 'Cargando...')
          : t('filters.select_client', 'Seleccionar cliente'),
        minWidth: '180px',
        maxWidth: '220px',
      },
      {
        key: 'cod_payout_id',
        label: t('filters.payout', 'Liquidación'),
        type: 'select',
        searchFn: payoutsSearchFn,
        onLoadMore: loadMorePayouts,
        hasMore: hasMorePayouts,
        allOption: {
          label: t('filters.all_payouts', 'Todas las liquidaciones'),
          value: 'all',
        },
        isLoadingOptions: isLoadingPayouts,
        placeholder: isLoadingPayouts
          ? t('filters.loading_payouts', 'Cargando...')
          : t('filters.select_payout', 'Seleccionar liquidación'),
        minWidth: '180px',
        maxWidth: '220px',
      },
      {
        key: 'without_payout',
        label: t('filters.without_payout', 'Sin liquidación'),
        type: 'checkbox',
        checkboxConfig: {
          label: t('filters.without_payout', 'Sin liquidación'),
        },
        minWidth: '150px',
      },
      {
        key: 'date_from',
        label: t('filters.date_from', 'Fecha desde'),
        type: 'date',
        dateConfig: {
          placeholder: t('filters.select_date', 'Seleccionar fecha'),
        },
        minWidth: '200px',
      },
      {
        key: 'date_to',
        label: t('filters.date_to', 'Fecha hasta'),
        type: 'date',
        dateConfig: {
          placeholder: t('filters.select_date', 'Seleccionar fecha'),
        },
        minWidth: '200px',
      },
    ],
    [t, clients, isLoadingClients, payoutsSearchFn, loadMorePayouts, hasMorePayouts, isLoadingPayouts],
  );

  // 🎯 Convertir valores de filtros a formato que SimpleFilter espera
  // SimpleFilter espera strings para selects, pero nuestro modelo usa números
  // Nota: ref ya no está aquí porque está fuera del SimpleFilter
  const filterValues = useMemo(() => {
    const values: Record<string, any> = {
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
      without_payout: filters.without_payout || false,
      client_id: filters.client_id ? String(filters.client_id) : 'all',
      cod_payout_id: filters.cod_payout_id ? String(filters.cod_payout_id) : 'all',
    };
    return values;
  }, [filters]);

  // 🎯 Handler para cambios en los filtros
  const handleFilterChange = (newValues: Partial<Record<string, any>>) => {
    const updatedFilters: Partial<PayoutItemsFilters> = {};

    // Convertir valores de vuelta al formato del modelo
    Object.keys(newValues).forEach((key) => {
      const value = newValues[key];

      if (key === 'client_id') {
        updatedFilters.client_id = value === 'all' || !value ? undefined : parseInt(value, 10);
      } else if (key === 'cod_payout_id') {
        updatedFilters.cod_payout_id = value === 'all' || !value ? undefined : parseInt(value, 10);
      } else if (key === 'without_payout') {
        updatedFilters.without_payout = value || undefined;
      } else if (key === 'date_from' || key === 'date_to') {
        updatedFilters[key as 'date_from' | 'date_to'] = value || undefined;
      }
    });

    onFiltersChange(updatedFilters);
  };

  return (
    <>
      <Search
        placeholder={t('filters.ref_placeholder', 'Buscar por ref...')}
        value={searchValue}
        onChange={handleSearchChange}
        onEnter={handleSearchSubmit}
        dataTestId="search-ref"
        inputDataTestId="search-ref-input"
      />
      <SimpleFilter
        useModal={true}
        fields={fields}
        values={filterValues}
        onChange={handleFilterChange}
        onReset={handleReset}
        isLoading={isFetching}
        loadingText={t('filters.searching', 'Buscando...')}
        clearButtonText={t('filters.clear', 'Limpiar')}
        triggerText={t('filters.title', 'Filtros')}
        translations={{
          filters: t('filters.title', 'Filtros'),
          searching: t('filters.searching', 'Buscando...'),
          selectDate: t('filters.select_date', 'Seleccionar fecha'),
          clear: t('filters.clear', 'Limpiar'),
        }}
      />
      {exportColumns && <ExportButton filters={filters} columns={exportColumns} />}
    </>
  );
};
